import React from 'react';
import { Icon } from '@iconify/react';
import type { UniBoxConfig, UniBoxControlState, UniBoxEntity, UniBoxThread } from './types';
import { getStatusDef } from './taxonomy';

const CONTROL_META: Record<UniBoxControlState, { label: string; icon: string; className: string }> = {
  ai_running: { label: 'AI running', icon: 'bx:bot', className: 'bg-white/[0.035] text-gray-400' },
  human_takeover: { label: 'You', icon: 'bx:user', className: 'bg-white/[0.055] text-white/78' },
  escalated: { label: 'Needs you', icon: 'bx:error-circle', className: 'bg-amber-400/[0.08] text-amber-100/86' },
};

const getChannelAccent = (channel: string, getChannelMeta: (c: string) => { label: string; icon: string; accent: string; soft: string }) =>
  getChannelMeta(channel);

/**
 * MessagePreviewContent — the conversation surface (header, control banner,
 * thread, AI suggestions, composer). Shared by the Inbox split pane and the
 * Calendar/Kanban slide-over drawer so the two never diverge.
 *
 * Pure/presentational: all state and handlers are passed in from the UniBox
 * owner, which owns the thread cache and composer value.
 */
export interface MessagePreviewContentProps {
  config: UniBoxConfig;
  entity: UniBoxEntity;
  activeChannel: string;
  onActiveChannelChange: (channel: string) => void;
  controlState: UniBoxControlState;
  thread: UniBoxThread | undefined;
  channelMeta: (channel: string) => { label: string; icon: string; accent: string; soft: string };
  composerValue: string;
  onComposerChange: (value: string) => void;
  onSend: () => void;
  composerLocked: boolean;
  suggestions: string[];
  suggestionVisible: boolean;
  canUseSuggestions: boolean;
  onLoadSuggestions: () => void;
  onApplySuggestion: (value: string) => void;
  onTakeOver: () => void;
  onReturnToAi: () => void;
  onClose?: () => void;
  onOpenLead?: () => void;
}

const EmailThreadBlock: React.FC<{
  config: UniBoxConfig;
  entity: UniBoxEntity;
  messages: UniBoxThread['messages'];
  formatFullDate: (value: string) => string;
  formatScheduledAt: (value: string) => string;
  formatEmailAddress: (name: string) => string;
  getEmailSubject: (config: UniBoxConfig, entity: UniBoxEntity, message: UniBoxThread['messages'][number], index: number) => string;
  getMessageTime: (message: UniBoxThread['messages'][number]) => string;
}> = ({ config, entity, messages, formatFullDate, formatScheduledAt, formatEmailAddress, getEmailSubject, getMessageTime }) => (
  <div className="space-y-5">
    {messages.map((message, index) => {
      const outbound = message.direction === 'outbound';
      const scheduled = message.state === 'scheduled';
      const fromName = outbound ? (message.sent_by === 'human' ? 'You' : 'Brainiacs AI') : entity.client_name;
      const toName = outbound ? entity.client_name : 'Brainiacs';
      const timestamp = getMessageTime(message);
      const subject = getEmailSubject(config, entity, message, index);
      const paragraphs = message.body.split(/\n{2,}/).filter(Boolean);

      return (
        <article
          key={`${message.id || 'scheduled'}-${index}`}
          className={`rounded-2xl px-5 py-5 ${scheduled ? 'bg-white/[0.045] shadow-[inset_3px_0_0_rgba(255,255,255,0.22)]' : 'bg-white/[0.02]'}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 pb-4 shadow-[inset_0_-1px_0_rgba(255,255,255,0.024)]">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                  <Icon icon={outbound ? (message.sent_by === 'ai' ? 'bx:bot' : 'bx:user') : 'bx:envelope'} className="h-3 w-3" />
                  {outbound ? (message.sent_by === 'ai' ? 'AI sent' : 'Human sent') : 'Received'}
                </span>
                {message.sequence_step && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.03] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
                    <Icon icon="bx:target-lock" className="h-3 w-3" />
                    {config.sequence_labels?.[message.sequence_step] || `Step ${message.sequence_step}`}
                  </span>
                )}
              </div>
              <h4 className="truncate text-xl font-semibold tracking-tight text-white">{subject}</h4>
            </div>
            {scheduled && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.07] px-2.5 py-1 text-[11px] font-bold text-white/82">
                <Icon icon="bx:time-five" className="h-3.5 w-3.5" />
                Queued
              </span>
            )}
          </div>

          <div className="grid gap-2 py-4 text-xs shadow-[inset_0_-1px_0_rgba(255,255,255,0.024)] sm:grid-cols-[72px_minmax(0,1fr)]">
            <span className="font-black uppercase tracking-[0.16em] text-white/28">From</span>
            <span className="min-w-0 truncate text-gray-300">
              {fromName} <span className="text-white/28">&lt;{formatEmailAddress(fromName)}&gt;</span>
            </span>
            <span className="font-black uppercase tracking-[0.16em] text-white/28">To</span>
            <span className="min-w-0 truncate text-gray-300">
              {toName} <span className="text-white/28">&lt;{formatEmailAddress(toName)}&gt;</span>
            </span>
            <span className="font-black uppercase tracking-[0.16em] text-white/28">{scheduled ? 'Send' : 'Date'}</span>
            <span className="text-gray-300">{timestamp ? formatFullDate(timestamp) : 'Pending'}</span>
          </div>

          <div className="space-y-5 pt-5 text-sm leading-7 text-gray-300">
            {paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {scheduled && message.scheduled_at && (
              <p className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.055] px-3 py-1.5 text-xs font-semibold text-white/72">
                <Icon icon="bx:time-five" className="h-3.5 w-3.5" />
                Scheduled for {formatScheduledAt(message.scheduled_at)}
              </p>
            )}
          </div>
        </article>
      );
    })}
  </div>
);

const TimelineMessageBlock: React.FC<{
  config: UniBoxConfig;
  message: UniBoxThread['messages'][number];
  formatScheduledAt: (value: string) => string;
  formatShortTime: (value: string) => string;
  getMessageTime: (message: UniBoxThread['messages'][number]) => string;
}> = ({ config, message, formatScheduledAt, formatShortTime, getMessageTime }) => {
  const outbound = message.direction === 'outbound';
  const scheduled = message.state === 'scheduled';
  const sentByAi = message.sent_by === 'ai';
  const sequenceLabel = message.sequence_step ? config.sequence_labels?.[message.sequence_step] || `Step ${message.sequence_step}` : '';

  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[82%] ${outbound ? 'items-end' : 'items-start'} flex flex-col gap-1.5`}>
        {message.sequence_step && (
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/35">
            <Icon icon="bx:target-lock" className="h-3 w-3" />
            {sequenceLabel}
          </span>
        )}
        <div
          className={`rounded-2xl px-4 py-3 text-sm leading-6 ${
            scheduled ? 'bg-white/[0.06] text-white/82 shadow-[inset_3px_0_0_rgba(255,255,255,0.22)]' : outbound ? 'bg-white/[0.06] text-gray-100' : 'bg-white/[0.045] text-gray-100'
          }`}
        >
          <p>{message.body}</p>
          {scheduled && message.scheduled_at && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/58">
              <Icon icon="bx:time-five" className="h-3.5 w-3.5" />
              Scheduled for {formatScheduledAt(message.scheduled_at)}
            </p>
          )}
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/30">
          {outbound && (
            <>
              <Icon icon={sentByAi ? 'bx:bot' : 'bx:user'} className="h-3 w-3" />
              {sentByAi ? 'AI' : 'Human'}
            </>
          )}
          {!outbound && 'Received'}
          {getMessageTime(message) && <span className="normal-case tracking-normal">/ {formatShortTime(getMessageTime(message))}</span>}
        </span>
      </div>
    </div>
  );
};

export const MessagePreviewContent: React.FC<MessagePreviewContentProps> = ({
  config,
  entity,
  activeChannel,
  onActiveChannelChange,
  controlState,
  thread,
  channelMeta,
  composerValue,
  onComposerChange,
  onSend,
  composerLocked,
  suggestions,
  suggestionVisible,
  canUseSuggestions,
  onLoadSuggestions,
  onApplySuggestion,
  onTakeOver,
  onReturnToAi,
  onClose,
  onOpenLead,
}) => {
  const activeMeta = getChannelAccent(activeChannel, channelMeta);
  const control = CONTROL_META[controlState];

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.006))]">
      {/* Subtle ambient tint behind the reading pane — just enough to separate
          it from the board/list without competing with the content. */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.03),transparent_55%)]" />
      <div className="relative flex min-h-0 flex-col">
      <div className="shrink-0 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/[0.045]">
              <Icon icon={activeMeta.icon} className={`h-5 w-5 ${activeMeta.accent}`} />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-lg font-bold text-white">{entity.client_name}</h3>
              <p className={`text-xs font-semibold ${activeMeta.accent}`}>{activeMeta.label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${control.className}`}>
              <Icon icon={control.icon} className="h-3.5 w-3.5" />
              {control.label}
            </span>
            <div className="flex items-center gap-1">
              {onOpenLead && (
                <button type="button" onClick={onOpenLead} className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.045] px-2.5 py-1.5 text-[11px] font-bold text-white/70 transition-all hover:bg-white/[0.075] hover:text-white" title="Open full lead details">
                  <Icon icon="bx:user-detail" className="h-3.5 w-3.5" />
                  Lead details
                </button>
              )}
              {onClose && (
                <button type="button" onClick={onClose} aria-label="Close preview" className="flex h-8 w-8 items-center justify-center rounded-lg text-white/40 transition-colors hover:bg-white/[0.06] hover:text-white">
                  <Icon icon="bx:x" className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {entity.channels.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {entity.channels.map((channel) => {
              const meta = channelMeta(channel);
              const active = channel === activeChannel;
              return (
                <button
                  key={channel}
                  onClick={() => onActiveChannelChange(channel)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition-all ${
                    active ? 'bg-white/[0.07] text-white' : 'text-gray-400 hover:bg-white/[0.04] hover:text-gray-200'
                  }`}
                >
                  <Icon icon={meta.icon} className={`h-3.5 w-3.5 ${meta.accent}`} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="shrink-0 px-5 pb-3">
        {controlState === 'human_takeover' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/[0.045] px-4 py-3">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-100">
              <Icon icon="bx:user" className="h-4 w-4 text-white/55" />
              You took over. AI paused.
            </span>
            <button onClick={onReturnToAi} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-black transition-all hover:bg-gray-100">
              <Icon icon="bx:bot" className="h-3.5 w-3.5" />
              Return to AI
            </button>
          </div>
        )}
        {controlState === 'escalated' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-400/[0.075] px-4 py-3">
            <span className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold text-amber-50">
              <Icon icon="bx:error-circle" className="h-4 w-4 shrink-0 text-amber-200" />
              <span className="truncate">AI escalated — needs you</span>
            </span>
            <button onClick={onTakeOver} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-black transition-all hover:bg-gray-100">
              <Icon icon="bx:user" className="h-3.5 w-3.5" />
              Take over
            </button>
          </div>
        )}
        {controlState === 'ai_running' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/[0.025] px-4 py-3">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300">
              <Icon icon="bx:bot" className="h-4 w-4 text-white/45" />
              AI is handling this conversation
            </span>
            <button onClick={onTakeOver} className="inline-flex items-center gap-2 rounded-full bg-white/[0.045] px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-white transition-all hover:bg-white/[0.075]">
              <Icon icon="bx:user" className="h-3.5 w-3.5" />
              Take over
            </button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className={activeChannel === 'email' ? 'space-y-5' : 'space-y-4'}>
          {activeChannel === 'email' && thread && (
            <EmailThreadBlock
              config={config}
              entity={entity}
              messages={thread.messages}
              // formatters are injected by the owner via props below — but here
              // we render through the shared helpers the owner passes.
              formatFullDate={(v) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(v))}
              formatScheduledAt={(v) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(v))}
              formatEmailAddress={(name) => `${name.toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '') || 'contact'}@example.company`}
              getEmailSubject={(cfg, ent, msg, index) => {
                const sub = ent.substatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                if (msg.state === 'scheduled') return `${cfg.entity_label} follow-up scheduled`;
                if (index > 0) return `Re: ${ent.client_name} - ${sub}`;
                const def = getStatusDef(ent.status);
                return `${ent.client_name} - ${def.key === ent.status ? def.label : sub}`;
              }}
              getMessageTime={(msg) => msg.sent_at || msg.received_at || msg.scheduled_at || ''}
            />
          )}
          {activeChannel !== 'email' &&
            (thread?.messages || []).map((message, index) => (
              <TimelineMessageBlock
                key={`${message.id || 'scheduled'}-${index}`}
                config={config}
                message={message}
                formatScheduledAt={(v) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(v))}
                formatShortTime={(v) => new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(v))}
                getMessageTime={(msg) => msg.sent_at || msg.received_at || msg.scheduled_at || ''}
              />
            ))}
          {!thread && <div className="flex h-48 items-center justify-center text-sm text-white/40">Loading conversation...</div>}
          {thread && thread.messages.length === 0 && <div className="flex h-48 items-center justify-center text-sm text-white/40">No messages yet</div>}
        </div>
      </div>

      <div className="shrink-0 space-y-3 px-5 pt-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-white/35">AI Suggests</p>
          <button
            onClick={onLoadSuggestions}
            disabled={!canUseSuggestions}
            className="inline-flex items-center gap-2 rounded-full bg-white/[0.045] px-3 py-1.5 text-[11px] font-bold text-white/70 transition-all hover:bg-white/[0.075] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Icon icon="bx:star" className="h-3.5 w-3.5" />
            AI Suggest
          </button>
        </div>

        {suggestionVisible && (
          <div className="flex flex-wrap justify-end gap-2">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => onApplySuggestion(suggestion)}
                className="max-w-full truncate rounded-full bg-white/[0.06] px-3 py-2 text-left text-xs font-medium text-white/82 transition-all hover:bg-white/[0.085] md:max-w-[76%]"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        <div className={`flex items-end gap-2 rounded-2xl bg-white/[0.045] p-2 transition-colors ${composerLocked ? 'opacity-65' : 'focus-within:bg-white/[0.06]'}`}>
          <textarea
            value={composerValue}
            onChange={(event) => onComposerChange(event.target.value)}
            disabled={composerLocked}
            rows={activeChannel === 'email' ? 3 : 1}
            placeholder={composerLocked ? 'AI is handling this conversation' : activeChannel === 'email' ? 'Write a reply...' : `Type a ${activeMeta.label} message...`}
            className="max-h-32 min-h-[44px] flex-1 resize-none bg-transparent px-2 py-2 text-sm leading-6 text-white outline-none placeholder:text-white/32 disabled:cursor-not-allowed"
          />
          <button
            onClick={onSend}
            disabled={composerLocked || !composerValue.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-black transition-all hover:scale-[1.03] hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/30"
            type="button"
            aria-label="Send message"
          >
            <Icon icon="bx:send" className="h-5 w-5" />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MessagePreviewContent;
